import classnames from 'classnames';
import { InkSigil } from '@sorrem-client/ui/components';
import { useAppNavigate, useCurrentPlayerCharacter } from '@sorrem-client/ui/hooks';
import { useCommissionAcceptance } from '@sorrem-client/ui/features/CharacterHome/components/CommissionOffers';
import { isCommissionGoalVisible, type CharacterCommission, type Commission, type JournalEntry } from '@sorrem/shared';
import {
    describeCommissionStatus,
    formatJournalDate,
    resolveJournalEntrySketch,
    formatJournalEntryTitle,
    toJournalReplayPath,
    resolveSelectedJournalEntry,
} from '../../journal-entry-text';
import { FieldJournal, JournalPage, JournalRibbon, PageHeading, PageNote } from '../FieldJournal';
import styles from './JournalBook.module.scss';

interface JournalBookProps {
    entries: readonly JournalEntry[];
    selectedEntryId: string | null;
    onSelectEntry: (entryId: string) => void;
    /**
     * Opens a vision in the Compendium, from the entry for the run that
     * surfaced it; null while the Compendium is closed, and the entry leaves
     * the vision out.
     */
    onOpenVision: ((visionId: string) => void) | null;
}

/**
 * The Journal, written from the front of the character's field journal:
 * dated entries down the left page, oldest first, each with its ink sketch;
 * the selected entry written out in full on the right, signed with their sigil,
 * with what can be done with it.
 */
export function JournalBook({ entries, selectedEntryId, onSelectEntry, onOpenVision }: JournalBookProps): JSX.Element {
    const selected = resolveSelectedJournalEntry(entries, selectedEntryId);

    return (
        <FieldJournal
            className={styles.journalBook}
            left={selected === null ? (
                <PageNote>Your story hasn't begun yet.</PageNote>
            ) : (
                <ol className={styles.entryList}>
                    {entries.map((entry) => (
                        <JournalEntryNote
                            key={entry.id}
                            entry={entry}
                            isSelected={entry.id === selected.id}
                            onSelect={() => onSelectEntry(entry.id)}
                        />
                    ))}
                </ol>
            )}
            right={selected !== null && <JournalEntryPage key={selected.id} entry={selected} onOpenVision={onOpenVision} />}
        />
    );
}

interface JournalEntryNoteProps {
    entry: JournalEntry;
    isSelected: boolean;
    onSelect: () => void;
}

/** One dated entry on the left page. */
function JournalEntryNote({ entry, isSelected, onSelect }: JournalEntryNoteProps): JSX.Element {
    const navigate = useAppNavigate();
    const sketch = resolveJournalEntrySketch(entry);

    return (
        // The heading button selects for keyboard users; a click anywhere on the entry does too.
        <li className={classnames(styles.entry, isSelected && styles.selected)} onClick={onSelect}>
            <button type="button" className={styles.entryHeading} aria-pressed={isSelected}>
                <span className={styles.entryDate}>{formatJournalDate(entry.at)}.</span>
                <span className={styles.entryTitle}>{formatJournalEntryTitle(entry)}</span>
            </button>

            <div className={styles.entryBody}>
                {sketch !== undefined && <img className={styles.sketch} src={sketch} alt="" draggable={false} />}
                <div className={styles.entryText}>
                    {entry.kind === 'cutscene' ? (
                        <p>{entry.cutscene.summary}</p>
                    ) : (
                        <>
                            <p>{entry.content.issuer}. {describeCommissionStatus(entry.commission)}</p>
                            <GoalChecklist commission={entry.commission} />
                        </>
                    )}
                </div>
            </div>

            {entry.kind === 'cutscene' && (
                <JournalRibbon
                    onClick={(event) => {
                        event.stopPropagation();
                        navigate(toJournalReplayPath(entry.cutscene.cutsceneId));
                    }}
                >
                    Replay
                </JournalRibbon>
            )}
        </li>
    );
}

/** The selected entry written out in full on the right page. */
function JournalEntryPage({ entry, onOpenVision }: { entry: JournalEntry; onOpenVision: ((visionId: string) => void) | null }): JSX.Element {
    const { character } = useCurrentPlayerCharacter();
    const vision = entry.kind === 'commission' ? entry.vision : undefined;

    return (
        <JournalPage>
            <PageHeading
                title={formatJournalEntryTitle(entry)}
                meta={entry.kind === 'cutscene' ? (
                    <>{formatJournalDate(entry.cutscene.seenAt)}.</>
                ) : (
                    <>
                        {entry.content.issuer}.<br />
                        {describeCommissionStatus(entry.commission)}
                    </>
                )}
            />

            <p className={styles.pageBody}>{entry.kind === 'cutscene' ? entry.cutscene.summary : entry.content.background}</p>

            {entry.kind === 'commission' && (
                <section>
                    <h4 className={styles.pageGoalsHeading}>Goals</h4>
                    <GoalChecklist commission={entry.commission} />
                </section>
            )}

            {entry.kind === 'commission' && entry.commission.status !== 'completed' && (
                <CommissionAction commission={entry.commission} content={entry.content} />
            )}

            {vision !== undefined && onOpenVision !== null && (
                <p className={styles.visionNote}>
                    When the heart broke, you saw something:{' '}
                    <button type="button" className={styles.visionLink} onClick={() => onOpenVision(vision.visionId)}>
                        {vision.title}
                    </button>
                </p>
            )}

            {character !== null && (
                <footer className={styles.signature}>
                    <InkSigil className={styles.signatureSigil} seed={character.id} />
                    <span className={styles.signatureName}>{character.name}</span>
                </footer>
            )}
        </JournalPage>
    );
}

/** Accepts an offered commission, or returns to an accepted one's run. */
function CommissionAction({ commission, content }: { commission: CharacterCommission; content: Commission }): JSX.Element {
    // An offered commission's meeting plays first, if it is still unseen (#330).
    const { isAccepting, accept } = useCommissionAcceptance();
    const offerCutsceneId = commission.status === 'offered' ? content.offerCutscene : undefined;

    return (
        <JournalRibbon disabled={isAccepting} onClick={() => void accept(commission.commissionId, offerCutsceneId)}>
            {commission.status === 'accepted' ? 'Return to the Crucible' : 'Accept Commission'}
        </JournalRibbon>
    );
}

/**
 * A commission's goals as a hand-ticked checklist. Goal progress lives in its
 * run, so outside one only a completed commission has its goals ticked.
 */
function GoalChecklist({ commission }: { commission: CharacterCommission }): JSX.Element {
    const isDone = commission.status === 'completed';
    return (
        <ul className={styles.goalList}>
            {/* Goals hidden until they unlock in the run stay hidden here. */}
            {commission.goals.filter((goal) => isCommissionGoalVisible(goal, undefined)).map((goal) => (
                <li key={goal.id} className={classnames(styles.goal, isDone && styles.goalDone)}>
                    <span className={styles.goalBox} role="img" aria-label={isDone ? 'Done' : 'Not done'} />
                    <span>{goal.description}</span>
                </li>
            ))}
        </ul>
    );
}
